import React from 'react';
import { useEffect, useState } from 'preact/hooks';
import { useForm } from 'react-hook-form';
import { Modal } from 'antd';
import { TextLarge, ButtonTheme, RenderForm, ModalTheme } from '../../../components/index';
import { ResponsivePageLayout } from '../../../components/page-layout/ResponsivePageLayout';
import { ContainerButton } from '../../../styles/global-style';
import { StyledContainer } from './styles';
import { useModal } from '../../../components/modal/hooks/useModal';
import { useResponsive } from '../../../hooks/useResponsive';
import { columns } from './columns/index';
import { useClaimApprove, useApproveNoDeduct, useRejectNoDeduct } from './hooks/useClaimApprove';
import { CustomerHistoryModal } from '../../../components/customer-history';
import { StyledStockTable } from '../claim-product/styles';
import { useStockAvailability } from '../claim-product/hooks/useStockAvailability';
import { STOCK_ROW_CLASS } from '../claim-product/resource/stockConst';
import { ApprovalFilters } from '../shared/ApprovalFilters';
import {
  APPROVAL_STOCK_ALL,
  countApprovalSelectedByItem,
  filterApprovalRowsByStock,
  getApprovalStockState,
} from '../shared/approvalStock';

const FORM_REMARK = [
  {
    name: 'remark',
    label: 'หมายเหตุ (ระบุได้ไม่เกิน 250 ตัวอักษร)',
    type: 'INPUT_AREA',
    span: 24,
    rules: {
      required: true,
      maxLength: 250,
    },
  },
];

export const ClaimApprove = () => {
  const [selectedClaimIds, setSelectedClaimIds] = useState([]);
  const [search, setSearch] = useState('');
  const [stockStatus, setStockStatus] = useState(APPROVAL_STOCK_ALL);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const { open: openRemark, onToggle: onToggleRemark } = useModal();
  const { open: openHistory, onToggle: onToggleHistory } = useModal();
  const { control, setValue, handleSubmit, reset } = useForm({ defaultValues: { remark: '' } });
  const { isMobile } = useResponsive();

  const { dataSource, onFetch, loading } = useClaimApprove();
  const { stockMap, loading: stockLoading } = useStockAvailability(dataSource);
  const selectedByItem = countApprovalSelectedByItem(dataSource, selectedClaimIds);
  const filteredRows = filterApprovalRowsByStock(dataSource, stockStatus, stockMap, selectedByItem);

  const [modal, modalContext] = Modal.useModal();

  const { mutate: approveNoDeduct, isPending: isPendingApprove } = useApproveNoDeduct({
    modal,
    onSuccessCallBack: () => {
      setSelectedClaimIds([]);
      onFetch({ search });
      onToggleRemark({ defaultValues: {} });
    },
  });

  const { mutate: rejectNoDeduct, isPending: isPendingReject } = useRejectNoDeduct({
    onSuccessCallBack: (response, claimId) => {
      setSelectedClaimIds((prev) => prev.filter((id) => id !== claimId));
      onFetch({ search });
    },
  });

  useEffect(() => {
    onFetch({});
  }, []);

  const onOpenApprove = () => {
    setRejectTarget(null);
    reset({ remark: '' });
    onToggleRemark({ defaultValues: {} });
  };

  const onOpenReject = (record) => {
    setRejectTarget(record.claimId);
    reset({ remark: '' });
    onToggleRemark({ defaultValues: {} });
  };

  const onSubmitRemark = (values) => {
    if (rejectTarget) {
      rejectNoDeduct({ claimId: rejectTarget, remark: values.remark });
    } else {
      approveNoDeduct({ claimIdList: selectedClaimIds, remark: values.remark });
      return;
    }
    onToggleRemark({ defaultValues: {} });
  };

  const onShowHistory = (record) => {
    setHistoryTarget(record);
    onToggleHistory({ record });
  };

  const onToggleSelect = (claimId) => {
    setSelectedClaimIds((prev) =>
      prev.includes(claimId) ? prev.filter((id) => id !== claimId) : [...prev, claimId],
    );
  };

  const rowSelection = {
    selectedRowKeys: selectedClaimIds,
    onChange: (selectedRowKeys) => setSelectedClaimIds(selectedRowKeys),
  };

  const onSearch = ({
    search: nextSearch,
    stockStatus: nextStockStatus,
    reset: clearSelection,
  }) => {
    if (nextSearch !== search || clearSelection) setSelectedClaimIds([]);
    setSearch(nextSearch);
    setStockStatus(nextStockStatus);
    onFetch({ search: nextSearch });
  };

  return (
    <StyledContainer>
      <TextLarge text="อนุมัติไม่ตัดประกัน" />
      {/* ไม่ครอบ CardTheme ที่นี่: ResponsiveTableLayout ครอบให้อยู่แล้วบน desktop
          ครอบซ้อนจะได้ขอบและ padding สองชั้นโดยที่ชั้นนอกไม่มีเนื้อหาอะไรเลย */}
      <ApprovalFilters onSearch={onSearch} />
      <StyledStockTable>
        <ResponsivePageLayout
          tableLayout={{
            columns: columns({
              onReject: onOpenReject,
              isMobile,
              selectedClaimIds,
              onToggleSelect,
              onShowHistory,
              stockMap,
              selectedByItem,
            }),
            dataSource: filteredRows,
            hasPagination: true,
            loading: loading || stockLoading,
            rowKey: 'claimId',
            rowSelection,
            rowClassName: (record) => {
              const { state } = getApprovalStockState(record, stockMap, selectedByItem);
              return STOCK_ROW_CLASS[state] ?? '';
            },
            extraContent: (
              <ContainerButton right>
                <ButtonTheme
                  useFor="CONFIRM"
                  title="อนุมัติ"
                  disabled={selectedClaimIds.length === 0 || isPendingApprove}
                  onClick={onOpenApprove}
                />
              </ContainerButton>
            ),
          }}
        />
      </StyledStockTable>
      {openRemark && (
        <ModalTheme
          title={
            rejectTarget ? 'ไม่อนุมัติแบบไม่ตัดประกัน (คืนเป็นเคลมปกติ)' : 'อนุมัติไม่ตัดประกัน'
          }
          open={openRemark}
          onToggle={onToggleRemark}
        >
          <RenderForm control={control} forms={FORM_REMARK} setValue={setValue} />
          <ContainerButton right>
            <ButtonTheme
              useFor="SUBMIT"
              onClick={handleSubmit(onSubmitRemark)}
              disabled={isPendingApprove || isPendingReject}
            />
            <ButtonTheme useFor="CANCEL" onClick={onToggleRemark} disabled={isPendingApprove} />
          </ContainerButton>
        </ModalTheme>
      )}
      {openHistory && (
        <CustomerHistoryModal
          open={openHistory}
          onToggle={onToggleHistory}
          initialValues={historyTarget}
        />
      )}
      {modalContext}
    </StyledContainer>
  );
};
